/** Client HTTP : cookie de session (HttpOnly) + jeton CSRF en double soumission sur les requêtes mutantes. */

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : (detail as { message?: string })?.message ?? `Erreur ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

function csrfToken(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)crm_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const method = (init.method ?? (init.json !== undefined ? "POST" : "GET")).toUpperCase();
  const headers = new Headers(init.headers);
  if (init.json !== undefined) headers.set("Content-Type", "application/json");
  if (method !== "GET" && method !== "HEAD") {
    const t = csrfToken();
    if (t) headers.set("X-CSRF-Token", t);
  }
  const res = await fetch(`/api${path}`, {
    ...init,
    method,
    headers,
    credentials: "same-origin",
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") ?? "";
  const body = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/auth/login")) onUnauthorized?.();
    const detail = (body as { detail?: unknown })?.detail ?? body;
    throw new ApiError(res.status, formatDetail(detail));
  }
  return body as T;
}

function formatDetail(d: unknown): unknown {
  // Erreurs de validation FastAPI : [{loc, msg}] → texte lisible
  if (Array.isArray(d)) return d.map((e) => (e as { msg?: string }).msg ?? String(e)).join(" · ");
  return d;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Erreur inattendue";
}
